import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {THEME} from '@wandb/weave/common/css/color.styles';
import {
  hexToRGB,
  OBLIVION,
  WHITE,
} from '@wandb/weave/common/css/globals.styles';
import {IconPennantPillFilled} from '@wandb/weave/components/Icon';
import React, {memo, useEffect, useMemo} from 'react';
import {Sparklines, SparklinesBars, SparklinesLine} from 'react-sparklines';

import {
  Analytics,
  trackMediaPanelSnapFromChartHotkey,
  trackPlotKeyboardCopy,
} from '../../services/analytics';
import {ColorPaletteOption, getTextColor} from '../../util/colors';
import {
  legendTemplateInsertCrosshairValues,
  legendTemplateRemoveCrosshairValues,
} from '../../util/legend';
import {type Mark} from '../../util/plotHelpers/types';
import {useColorMode} from '../../util/useColorMode';
import {type TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {markToIconNameMap} from '../LineStylePicker';
import {usePanelConfigContext} from '../PanelRunsLinePlot/PanelConfigContext';
import {useWorkspaceSettingsColorPalette} from '../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {getCascadingSetting} from '../WorkspaceDrawer/Settings/getCascadingSetting';
import {
  useMaybeLinePlotShowXRange,
  useMaybeMediaCardTypes,
  useMaybeSetWorkspaceSliderSteps,
} from '../WorkspaceDrawer/Settings/hooks';
import {useSectionSettingsContextMaybe} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {TooltipNumberOfRunsOptions} from '../WorkspaceDrawer/Settings/types';
import {
  type MarkSeriesPointForCrosshair,
  TOOLTIP_FONT_FAMILY,
  TOOLTIP_FONT_SIZE,
  TOOLTIP_LINE_HEIGHT_RUN_NAMES,
} from './crosshairUtils';
import {GradientOverlay} from './GradientOverlay';
import {getHotKey, getNeighborsIndexRange} from './utils';

const copyTsvFormattedDataToClipboard = (
  crosshairValues: MarkSeriesPointForCrosshair[]
): void => {
  const tsvFormattedData = crosshairValues
    .map(v => legendTemplateRemoveCrosshairValues(v.title) + '\t' + v.y)
    .join('\n');

  navigator.clipboard.writeText(tsvFormattedData);
  trackPlotKeyboardCopy();
};

/**
 * Check if a crosshair value matches the highlighted run.
 * In multi-metric mode, also requires the metric to match.
 */
function isHighlightMatch(
  val: MarkSeriesPointForCrosshair,
  highlightRun: string | undefined,
  highlightMetric: string | symbol | undefined,
  isMultiMetric: boolean
): boolean {
  const runMatch = val.uniqueId === highlightRun;
  if (isMultiMetric && highlightMetric != null) {
    return runMatch && val.metricName === highlightMetric;
  }
  return runMatch;
}

const getHandleKeyPress = (
  crosshairValues: MarkSeriesPointForCrosshair[],
  highlightRun: string | undefined,
  setWorkspaceSliderSteps:
    | ((
        stepIndex: number,
        mediaSliderKey: string,
        sectionSliderSync: boolean,
        forceSync: boolean
      ) => void)
    | undefined,
  xAxis: string
): EventListener => {
  return (e: unknown) => {
    if ((e as Event).type !== 'keydown') {
      // ruh roh!
      console.error(
        'Did not receive keydown event in keydown event handler!??!'
      );
    }
    const keyboardEvent = e as KeyboardEvent;
    if (
      keyboardEvent.key === 'c' &&
      (keyboardEvent.metaKey || keyboardEvent.ctrlKey) &&
      !keyboardEvent.repeat
    ) {
      copyTsvFormattedDataToClipboard(crosshairValues);
    }
    if (
      keyboardEvent.key === 'S' &&
      keyboardEvent.shiftKey &&
      crosshairValues.length > 0 &&
      !keyboardEvent.repeat
    ) {
      const crosshairValueForRun =
        crosshairValues.find(v => v.uniqueId === highlightRun) ??
        crosshairValues[0];
      const step = crosshairValueForRun.x;
      trackMediaPanelSnapFromChartHotkey({
        xAxis,
      });
      setWorkspaceSliderSteps?.(step, xAxis, false, true);
    }
  };
};

type CrosshairFlagContentProps = {
  allowCmdClickRun: boolean;
  allowCopyContents: boolean;
  crosshairValues: MarkSeriesPointForCrosshair[];
  highlightMetric?: string | symbol;
  highlightRun?: string;
  isHovered: boolean;
  isSparkLines: boolean;
  maxLength?: number;
  truncateLegend: boolean;
  xAxis: string;
  runNameTruncationType?: TruncationType;
};

const hardcodedRelative: React.CSSProperties = {position: 'relative'};
const hardcodedStyles: React.CSSProperties = {width: '100%', height: '100%'};
const CrosshairFlagContentComp: React.FC<CrosshairFlagContentProps> = ({
  allowCmdClickRun,
  allowCopyContents,
  crosshairValues,
  highlightMetric,
  highlightRun,
  isHovered: isHoveredPanel = false,
  isSparkLines = false,
  maxLength,
  truncateLegend,
  xAxis,
  runNameTruncationType,
}) => {
  'use memo';
  const {
    colorRunNames,
    highlightedCompanionRunOnly,
    isMultiMetric,
    tooltipNumberOfRuns,
    xAxisFormat,
    xAxis: nonPrettifiedXAxis, // we need it as "_step" not "Step" to match the media panel slider key
  } = usePanelConfigContext();
  const mediaCardTypes = useMaybeMediaCardTypes();
  const shouldShowMediaSnapToStepHotKey =
    mediaCardTypes != null && mediaCardTypes.length > 0;
  const workspaceShowXRange = useMaybeLinePlotShowXRange();
  const sectionSettings = useSectionSettingsContextMaybe();
  const showXRange = getCascadingSetting(
    [workspaceShowXRange, sectionSettings?.linePlot?.showXRange],
    false
  );

  let minimumNeighbors = 0;
  if (!isHoveredPanel && highlightedCompanionRunOnly) {
    minimumNeighbors = 0;
  } else if (tooltipNumberOfRuns === TooltipNumberOfRunsOptions.Single) {
    minimumNeighbors = 0;
  } else if (tooltipNumberOfRuns === TooltipNumberOfRunsOptions.Default) {
    minimumNeighbors = 4;
  } else if (tooltipNumberOfRuns === TooltipNumberOfRunsOptions.All) {
    minimumNeighbors = Infinity;
  }

  const setWorkspaceSliderSteps = useMaybeSetWorkspaceSliderSteps();

  useEffect(() => {
    let cleanup;
    if (allowCmdClickRun || allowCopyContents) {
      const fn = getHandleKeyPress(
        crosshairValues,
        highlightRun,
        setWorkspaceSliderSteps,
        nonPrettifiedXAxis
      );
      // Not a shortcut: detects modifier keys held during crosshair hover
      // oxlint-disable-next-line wandb/no-ad-hoc-keydown-listener
      document.addEventListener('keydown', fn, {/* no options */});
      cleanup = () => {
        document.removeEventListener('keydown', fn);
      };
    }
    return cleanup;
  }, [
    allowCmdClickRun,
    allowCopyContents,
    crosshairValues,
    highlightRun,
    nonPrettifiedXAxis,
    setWorkspaceSliderSteps,
  ]);

  const sortedValues = useMemo(() => {
    // Sort all runs by y value (descending)
    return crosshairValues
      .slice()
      .sort((a, b) => (b.y as number) - (a.y as number));
  }, [crosshairValues]);

  const matchedIndex = sortedValues.findIndex(v =>
    isHighlightMatch(v, highlightRun, highlightMetric, isMultiMetric)
  );

  const [minIndex, maxIndex] = React.useMemo(
    () =>
      getNeighborsIndexRange({
        neighborsPerSide: minimumNeighbors,
        list: sortedValues,
        index: matchedIndex,
      }),
    [sortedValues, matchedIndex, minimumNeighbors]
  );

  /**
   * We don't know if we're in multiple metrics mode so we gather the data to handle either in a single loop
   * Filtered: these are the items to display based on neighboring rules
   * Matched: the hovered run has multiple metrics logged, so we don't show neighbors we just show the exact matches
   *
   * Note: there is a design discussion in progress about how to handle multiple metrics. We experimented with showing just the matching run in the overlay (reverted here: https://github.com/wandb/core/pull/20659) but a customer asked for the revert. Leaving this here for now as we will revisit in the future.
   */
  const {filteredValues, matchedValues} = sortedValues.reduce(
    (acc, val, index) => {
      // Match on uniqueId (and metric in multi-metric mode)
      if (isHighlightMatch(val, highlightRun, highlightMetric, isMultiMetric)) {
        acc.matchedValues.push(val);
        acc.filteredValues.push(val);
        return acc;
      }

      // Show if within the neighbor range
      if (index >= minIndex && index <= maxIndex) {
        acc.filteredValues.push(val);
      }

      return acc;
    },
    {
      filteredValues: [] as MarkSeriesPointForCrosshair[],
      matchedValues: [] as MarkSeriesPointForCrosshair[],
    }
  );

  // Check if baseline exists but is outside the neighbor range
  const baselineIndex = sortedValues.findIndex(v => v.isBaseline);
  const baselineValue =
    baselineIndex >= 0 ? sortedValues[baselineIndex] : undefined;
  const baselineInFilteredValues = filteredValues.some(v => v.isBaseline);
  const showBaselineSeparately = baselineValue && !baselineInFilteredValues;

  // Determine if baseline is above (high value) or below (low value) the visible range
  const baselineAboveRange = baselineIndex >= 0 && baselineIndex < minIndex;
  const baselineBelowRange = baselineIndex > maxIndex;

  const sanitizedNeighbors = useMemo(() => {
    if (!baselineValue || !showBaselineSeparately) {
      return filteredValues;
    }
    const baselineId = baselineValue.uniqueId ?? baselineValue.title ?? '';
    return filteredValues.filter(neighbor => {
      const neighborId = neighbor.uniqueId ?? neighbor.title ?? '';
      // Drop the baseline from the neighbor list when it will be rendered separately
      return neighborId !== baselineId;
    });
  }, [baselineValue, filteredValues, showBaselineSeparately]);

  const neighborsToDisplay = useMemo(() => {
    if (!showBaselineSeparately || !baselineValue) {
      return sanitizedNeighbors;
    }
    if (sanitizedNeighbors.length <= 1) {
      // Don't trim if there's only one item (e.g., Single run mode) - we need to show the hovered run
      return sanitizedNeighbors;
    }
    if (baselineAboveRange) {
      // Baseline will be rendered above; trim the top-most neighbor to keep count stable
      return sanitizedNeighbors.slice(1);
    }
    if (baselineBelowRange) {
      // Baseline will be rendered below; trim the bottom-most neighbor to keep count stable
      return sanitizedNeighbors.slice(0, sanitizedNeighbors.length - 1);
    }
    return sanitizedNeighbors;
  }, [
    baselineAboveRange,
    baselineBelowRange,
    baselineValue,
    sanitizedNeighbors,
    showBaselineSeparately,
  ]);

  const valuesToRender = showBaselineSeparately
    ? neighborsToDisplay
    : filteredValues;

  const showGradients =
    sortedValues.length > valuesToRender.length && // gradients can't be active unless there are greater than nine runs
    valuesToRender.length > 5;

  /**
   * note: the maximum y values will have the lowest indexes because they sort in descending order. The values need to be sorted because in the plot the lines will be ordered from highest value (top of chart) to lowest value (bottom of chart) at any given x value and the overlay needs to match.
   */
  const showHiddenMaxValuesGradient = showGradients && minIndex > 0;
  const showHiddenMinValuesGradient =
    showGradients && maxIndex < sortedValues.length - 1;

  const fixedStyles: React.CSSProperties = useMemo(
    () => ({
      background: THEME.bgSurface,
      border: `1px solid ${THEME.border}`,
      borderRadius: 8,
      color: THEME.textPrimary,
      fontFamily: TOOLTIP_FONT_FAMILY,
      fontSize: TOOLTIP_FONT_SIZE,
      lineHeight: TOOLTIP_LINE_HEIGHT_RUN_NAMES,
      padding: '8px',
      position: 'relative',
      whiteSpace: 'nowrap',
      width: isSparkLines ? 200 : undefined,
    }),
    [isSparkLines]
  );

  const barStyles = useMemo(
    () => ({
      fill: crosshairValues[0].color,
      fillOpacity: 0.75,
    }),
    [crosshairValues]
  );

  const getIsHighlighted = useMemo(() => {
    return (point: MarkSeriesPointForCrosshair) => {
      if (
        isHighlightMatch(point, highlightRun, highlightMetric, isMultiMetric) &&
        crosshairValues != null && // no need to show highlight if only one run
        valuesToRender.length > 1 &&
        matchedValues.length === 1 // no highlighting with multiple metrics
      ) {
        return 'highlighted';
      }

      return '';
    };
  }, [
    crosshairValues,
    highlightMetric,
    highlightRun,
    isMultiMetric,
    matchedValues.length,
    valuesToRender.length,
  ]);
  const {isDarkMode} = useColorMode();
  const colorPaletteOption = useWorkspaceSettingsColorPalette();

  const getRowStyles = useMemo(() => {
    return (point: MarkSeriesPointForCrosshair): React.CSSProperties => {
      const isHighlighted = !!getIsHighlighted(point);
      const baseStyles: React.CSSProperties = {
        alignItems: 'center',
        display: 'flex',
        height: 20,
        paddingLeft: 4,
        paddingRight: 4,
        gap: 6,
        background: isHighlighted
          ? hexToRGB(isDarkMode ? WHITE : OBLIVION, 0.07)
          : 'none',
        fontWeight: isHighlighted ? 600 : 400,
      };

      if (colorRunNames && colorPaletteOption === ColorPaletteOption.DEFAULT) {
        baseStyles.color = getTextColor(point.color);
      } else if (isDarkMode) {
        baseStyles.color = THEME.textTertiary;
      }
      return baseStyles;
    };
  }, [colorRunNames, getIsHighlighted, isDarkMode, colorPaletteOption]);

  const hotKey = getHotKey();

  if (isSparkLines) {
    return (
      <div style={fixedStyles}>
        <Sparklines data={crosshairValues[0].values} style={hardcodedStyles}>
          <SparklinesLine color={crosshairValues[0].color} />
          <SparklinesBars barWidth={8} style={barStyles} />
        </Sparklines>
        <div>
          {crosshairValues.length > 0 && (
            <>
              {xAxis}: {crosshairValues[0].x}
            </>
          )}
        </div>
        {allowCopyContents && (
          <div
            className="break-words whitespace-normal text-wb-text-tertiary"
            style={{
              fontFamily: 'Inconsolata, monospace',
              fontSize: 12,
              lineHeight: '141%',
            }}>
            {hotKey}+C to copy tooltip contents
          </div>
        )}
      </div>
    );
  }

  // this keeps empty companion boxes from showing up on hover
  if (valuesToRender.length === 0) {
    return null;
  }

  const renderLegendRow = (
    point: MarkSeriesPointForCrosshair,
    key: string,
    dataTest: string
  ) => (
    <div
      data-test={dataTest}
      key={key}
      data-row-key="legend-row"

      style={getRowStyles(point)}>
      <LegacyWBIcon
        className="flex items-center justify-center text-lg shrink-0"
        style={{
          color: getTextColor(point.color),
        }}
        name={markToIconNameMap[(point.mark as Mark) || 'solid']}
      />

      <div className="flex flex-col justify-center">
        {legendTemplateInsertCrosshairValues({
          addDefaultTemplateIfCrosshairValuesMissing: true,
          key: point.name + key,
          legendTemplate: point.title,
          maxLength: maxLength || 10,
          truncateOutsideOfCrosshairValues: truncateLegend,
          type: 'line',
          values: {
            x: {
              xAxis,
              val: point.x,
              xAxisFormat: xAxisFormat,
              prevXVal: showXRange ? point.prevX : undefined,
            },
            y: point.value,
            mean: point.value,
            min:
              point.minmax && point.minmax.length >= 2
                ? point.minmax[0]
                : undefined,
            max:
              point.minmax && point.minmax.length >= 2
                ? point.minmax[1]
                : undefined,
            stddev: point.stddev,
            percent: point.percent,
            total: point.total,
            original: point.original,
          },
          runNameTruncationType,
        })}
      </div>

      {baselineValue && (
        <div className="ml-auto shrink-0 w-[14px] flex justify-end items-center">
          {point.isBaseline && (
            <IconPennantPillFilled
              width={10}
              height={13}
              data-test="tooltip-legend-baseline-icon"
              style={{
                color: point.color,
              }}
            />
          )}
        </div>
      )}
    </div>
  );

  return (
    <div data-test="line-plot-crosshair-content" style={fixedStyles}>
      <div style={hardcodedRelative}>
        {/* Show baseline above if it's outside range at top */}
        {showBaselineSeparately && baselineAboveRange && baselineValue && (
          <div
            className="mb-2 border-b border-wb-border pb-2"
            data-test="baseline-section-top">
            {/* Render baseline once above the neighbor list when it sits outside the top range */}
            {renderLegendRow(
              baselineValue,
              'baseline-separate-top',
              'line-plot-crosshair-content-row-baseline-separate'
            )}
          </div>
        )}
        <div className="relative">
          {showHiddenMaxValuesGradient && <GradientOverlay position="top" />}
          {neighborsToDisplay.map((point, i) =>
            renderLegendRow(
              point,
              `${point.uniqueId ?? point.title ?? 'row'}-${i}`,
              'line-plot-crosshair-content-row'
            )
          )}

          {showHiddenMinValuesGradient && <GradientOverlay position="bottom" />}
        </div>
        {showBaselineSeparately && baselineBelowRange && baselineValue && (
          <div
            className="mt-2 border-t border-wb-border pt-2"
            data-test="baseline-section-bottom">
            {/* Render baseline once below the neighbor list when it sits outside the bottom range */}
            {renderLegendRow(
              baselineValue,
              'baseline-separate-bottom',
              'line-plot-crosshair-content-row-baseline-separate'
            )}
          </div>
        )}
      </div>
      {(allowCopyContents || allowCmdClickRun) && (
        <div
          className="text-wb-text-tertiary"
          style={{
            fontFamily: 'Inconsolata, monospace',
            fontSize: 12,
            lineHeight: '141%',
          }}>
          {allowCopyContents && (
            <div id="copy-values-prompt">
              {hotKey}+C to copy tooltip contents
            </div>
          )}
          {allowCmdClickRun && <div>{hotKey}+click to view run in new tab</div>}
          {shouldShowMediaSnapToStepHotKey && (
            <div>Shift+S to snap media panels to this step</div>
          )}
        </div>
      )}
    </div>
  );
};
export const CrosshairFlagContent = memo(CrosshairFlagContentComp);
